// RouteMap.js

import React, { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { Card, CircularProgress, Box, Typography } from '@mui/material';
import useSummary from '../../lib/hooks/useSummary';
import L from 'leaflet';
import {useSearchParams} from "react-router-dom";

const FitBoundsToMarkers = ({ markers }) => {
   const map = useMap();
   
   useEffect(() => {
      if (!markers.length) return;
      const bounds = L.latLngBounds(
         markers.map(({ latitude, longitude }) => [latitude, longitude])
      );
      map.flyToBounds(bounds, { padding: [50, 50], duration: 1.5 });
   }, [markers, map]);
   
   return null;
};

// Fix the default icon issue with Leaflet
const DefaultIcon = L.icon({
   iconUrl: require('leaflet/dist/images/marker-icon.png'),
   iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
   shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
   iconSize: [25, 41],
   iconAnchor: [12, 41],
   popupAnchor: [1, -34],
   tooltipAnchor: [16, -28],
   shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

const RouteMap = () => {
   const [searchParams] = useSearchParams();
   const orderId = useMemo(() => searchParams.get("order"), [searchParams]);
   const { data, isLoading } = useSummary();
   const shipment = useMemo(() => {
      if (data?.orders && orderId) {
         return data.orders.find((order) => String(order.id) === String(orderId)) || null;
      }
      return data?.route_info || null;
   }, [data, orderId]);
   
   const pickupLocation = useMemo(() => shipment?.pickup?.location, [shipment]);
   const dropoffLocation = useMemo(() => shipment?.dropoff?.location, [shipment]);
   const checkinLocations = useMemo(() => shipment?.checkins || [], [shipment]);
   
   const allMarkers = [pickupLocation, ...checkinLocations.map((c) => c.location), dropoffLocation].filter(Boolean);
   
   if (isLoading) {
      return (
         <Card>
            <Box display="flex" justifyContent="center" alignItems="center" height={500}>
               <CircularProgress />
               <Typography variant="subtitle1" sx={{ ml: 2 }}>
                  Loading map…
               </Typography>
            </Box>
         </Card>
      );
   }
   
   return (
      <Card>
         <style>{`.leaflet-control-attribution.leaflet-control { display: none !important; }`}</style>
         <MapContainer center={[37.0902, -95.7129]} zoom={5} style={{ height: '500px', width: '100%' }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            
            {pickupLocation && (
               <Marker position={[pickupLocation.latitude, pickupLocation.longitude]}>
                  <Popup>
                     <Typography variant="subtitle1">Pickup Location</Typography>
                     <Typography>{shipment.pickup.location.address}</Typography>
                     <Typography>Contact: {shipment.pickup.location.contact_phone}</Typography>
                     <Typography>Expected Pickup: {new Date(shipment.pickup.date).toLocaleDateString()}</Typography>
                  
                  </Popup>
               </Marker>
            )}
            
            {checkinLocations.map((checkIn, idx) => (
               <Marker key={idx} position={[checkIn.location.latitude, checkIn.location.longitude]}>
                  <Popup>
                     <Typography variant="subtitle1">Check-In</Typography>
                     <Typography>{checkIn.remarks}</Typography>
                     {checkIn.location && (
                        <>
                           <Typography>{checkIn.location.address}</Typography>
                           {checkIn.location.contact_phone && (
                              <Typography>Contact: {checkIn.location.contact_phone}</Typography>
                           )}
                        </>
                     )}
                  </Popup>
               </Marker>
            ))}
            
            {dropoffLocation && (
               <Marker position={[dropoffLocation.latitude, dropoffLocation.longitude]}>
                  <Popup>
                     <Typography variant="subtitle1">Dropoff Location</Typography>
                     <Typography>{shipment.dropoff.location.address}</Typography>
                     <Typography>Contact: {shipment.dropoff.location.contact_phone}</Typography>
                     <Typography>Expected Delivery: {new Date(shipment.expectedDelivery).toLocaleDateString()}</Typography>
                  </Popup>
               </Marker>
            )}
            
            {allMarkers.length > 0 && <FitBoundsToMarkers markers={allMarkers} />}
         </MapContainer>
      </Card>
   );
};

export default RouteMap;
